import { useEffect, useRef, useState } from 'react'
import { LoadingSkeleton } from 'components'
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Pagination,
  MenuItem,
} from '@mui/material'
import moment from 'moment'
import Modal from 'features/modal/Modal'
import MenuImportHistoryDrawer from './MenuImportHistoryDrawer'
import { 
  fetchPartnerSyncLifecycles, 
  PartnerSyncLifecycleSummaryWithDuration,
} from 'api/partners/syncLifecycles'
import {
  ModalWrapper,
  ModalHeader,
  ModalTitle,
  ModalContent,
  EmptyStateWrapper,
  EmptyStateText,
  TablePaginationWrapper,
  ScrollableTableContainer,
  PaginationWrapper,
  CurrentPageInfo,
  PaginationText,
  PaginationSelect,
  RowsPerPageWrapper,
  TimestampText,
  StatusWrapper,
  StatusDot,
  StatusText,
} from './styles'

type Props = {
  isOpen: boolean
  onClose: () => void
  locationId: number
}

const MenuImportHistoryModal = ({ isOpen, onClose, locationId }: Props) => {
  const [data, setData] = useState<PartnerSyncLifecycleSummaryWithDuration[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedSyncLifecycleId, setSelectedSyncLifecycleId] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const lastFetchedId = useRef<number | null>(null)

  const handleRowClick = (record: PartnerSyncLifecycleSummaryWithDuration) => {
    setSelectedSyncLifecycleId(record.id)
    setIsDrawerOpen(true)
  }
  
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setSelectedSyncLifecycleId(null)
  }

  useEffect(() => {
    if (!isOpen || lastFetchedId.current === locationId) return

    const getData = async () => {
    setLoading(true)
    setError(null)
    lastFetchedId.current = locationId
    try {
      const lifecycleRecords = await fetchPartnerSyncLifecycles(locationId)
      setData(lifecycleRecords)
    } catch (err) {
      setError('Unable to fetch import history.')
    } finally {
      setLoading(false)
    }
  }

    getData()
  }, [isOpen, locationId])

  useEffect(() => {
    if (!isOpen) {
      lastFetchedId.current = null
    }
  }, [isOpen])

  const indexOfLastItem = currentPage * rowsPerPage
  const indexOfFirstItem = indexOfLastItem - rowsPerPage
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem)

  return (
    <Modal
      width='600px'
      height='auto'
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={true}
      closeOnOutsideClick={false}
    >
      <ModalWrapper>
        <ModalHeader>
          <ModalTitle>Menu Import History</ModalTitle>
        </ModalHeader>
        <ModalContent>
          {loading ? (
            <div data-testid='loading-skeleton'>
              <LoadingSkeleton lineCount={3} />
            </div>
          ) : error ? (
            <EmptyStateWrapper>
              <EmptyStateText>{error}</EmptyStateText>
            </EmptyStateWrapper>
          ) : data.length === 0 ? (
            <EmptyStateWrapper>
              <EmptyStateText>No menu import history available.</EmptyStateText>
            </EmptyStateWrapper>
          ) : (
            <TablePaginationWrapper>
              <ScrollableTableContainer>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Duration (s)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentItems.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <TimestampText onClick={() => handleRowClick(row)}>
                              {moment(row.start_timestamp).format('dddd, MM/DD/YYYY, h:mm A')}
                            </TimestampText>
                          </TableCell>
                          <TableCell>
                            <StatusWrapper>
                              <StatusDot status={row.aasm_state} />
                              <StatusText status={row.aasm_state}>{row.aasm_state}</StatusText>
                            </StatusWrapper>
                          </TableCell>
                          <TableCell>{row.duration}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </ScrollableTableContainer>

              <PaginationWrapper>
                <CurrentPageInfo>
                  {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, data.length)} of {data.length}
                </CurrentPageInfo>

                <Pagination
                  count={Math.ceil(data.length / rowsPerPage)}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  size='small'
                  shape='rounded'
                />

                <RowsPerPageWrapper>
                  <PaginationText>Results per page:</PaginationText>
                  <PaginationSelect
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </PaginationSelect>
                </RowsPerPageWrapper>
              </PaginationWrapper>
            </TablePaginationWrapper>
          )}

          {selectedSyncLifecycleId && (
            <MenuImportHistoryDrawer
              isOpen={isDrawerOpen}
              onClose={handleCloseDrawer}
              syncLifecycleId={selectedSyncLifecycleId}
            />
          )}
        </ModalContent>
      </ModalWrapper>
    </Modal>
  )
}

export default MenuImportHistoryModal
